import { cloneDeep, forEach, set, isNil, orderBy, first, isEmpty, some } from 'lodash';
import { Identifiable, Modifiable, Schedulable } from '../../utils';
import { AssignmentBasicInfo, AssignmentMeta, AssignmentType } from '../assignment';
import { FractionalIndex } from '../common/FractionalIndex';
import { BookmarkUpdateData } from './BookmarkUpdateData';

/**
 * Dit is een link tussen de user en content (assignment)
 * Studenten luisteren niet naar de assignment zelf maar juist naar de bookmarks.
 */
export interface Bookmark extends Modifiable, Identifiable, Schedulable {
  type: AssignmentType;
  userId: string;
  assignmentId: string;
  createdAt: number;
  isHidden: boolean;
  groupId?: string | undefined;
  module?: string | undefined;
  order?: FractionalIndex.OrderKey | undefined;
  /** copy of the entry seenAt */
  seenAt?: number;
  assignmentInfo: AssignmentBasicInfo;
  hasUnseenComments?: boolean;
  isArchived?: boolean;
}

export namespace Bookmark {
  export const excludedComparisonPaths: string[] = ['createdAt', 'modifiedAt'];

  export type Type = AssignmentType;

  export type WithModule = Bookmark & {
    groupId: NonNullable<Bookmark['groupId']>;
    module: NonNullable<Bookmark['module']>;
    order: NonNullable<Bookmark['order']>;
  };

  export function createId(assignmentId: string, userId: string): string {
    return `${assignmentId}-${userId}`;
  }

  export function create(data: AssignmentMeta.WithSettings, userId: string, creationDate?: number): Bookmark {
    const { meta, settings } = data;

    const now = creationDate || Date.now();

    return {
      _id: createId(meta._id, userId),
      type: data.meta.type,
      groupId: data.meta.groupId,
      module: data.meta.module,
      order: data.meta.order,
      userId,
      createdAt: now,
      modifiedAt: now,
      assignmentId: meta._id,
      assignmentInfo: meta.assignmentInfo,
      seenAt: undefined,
      isHidden: settings.type === 'realtime',
    };
  }

  /** creates an in memory copy with the updated changes so we can use it for comprison */
  export function update(bookmark: Bookmark, updates: BookmarkUpdateData): Bookmark {
    const cloned = cloneDeep(bookmark);
    const updateFields = BookmarkUpdateData.updateFields(updates);
    forEach(updateFields, (value, field) => set(cloned, field, value));
    return cloned;
  }

  export function orderedByCreationDate<T extends Bookmark>(bookmarks: T[]): T[] {
    return orderBy(bookmarks, (t) => t.createdAt, 'desc');
  }

  export function latestDate(bookmarks: Bookmark[]): Date | undefined {
    const bookmark = first(Bookmark.orderedByCreationDate(bookmarks));
    return bookmark && new Date(bookmark.createdAt);
  }

  /**
   * @deprecated this could be false in case of a 'test', which is not used anymore in the 2.0
   */
  export function isAccessible(bookmark: Bookmark): boolean {
    return true;
  }

  /**
   * @deprecated this could be true in case of a 'test', which is not used anymore in the 2.0
   */
  export function isLockedByOtherBookmarks(bookmark: Bookmark, lockedBookmarks: Bookmark[]): boolean {
    return false;
  }
}
